import React, { useState, useEffect } from 'react';
import { Clock, Save, ToggleLeft, ToggleRight } from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_LABELS = {
  monday: 'Pazartesi',
  tuesday: 'Salı',
  wednesday: 'Çarşamba',
  thursday: 'Perşembe',
  friday: 'Cuma',
  saturday: 'Cumartesi',
  sunday: 'Pazar'
};

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const SettingsManagement = ({ token }) => {
  const [hours, setHours] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchHours(); }, []);

  const fetchHours = async () => {
    try {
      const res = await axios.get(`${API}/settings/working-hours`);
      setHours(res.data.days);
    } catch (e) {
      toast.error('Çalışma saatleri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const updateDay = (day, field, value) => {
    setHours(prev => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
  };

  const toggleClosed = (day) => {
    setHours(prev => ({ ...prev, [day]: { ...prev[day], is_closed: !prev[day].is_closed } }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/working-hours`, { days: hours }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Çalışma saatleri güncellendi');
    } catch (e) {
      toast.error('Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-basil border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-basil/10 rounded-full flex items-center justify-center">
            <Clock className="w-5 h-5 text-basil" />
          </div>
          <div>
            <h2 className="font-playfair text-xl font-semibold text-charcoal">Çalışma Saatleri</h2>
            <p className="text-sm text-charcoal/60">Çalışma saatleri dışında sipariş alınmaz</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-basil text-white hover:bg-basil/90" data-testid="save-hours-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Kaydediliyor...' : 'Kaydet'}
        </Button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="grid grid-cols-[140px_1fr_1fr_80px] gap-4 p-4 bg-stone-50 border-b font-medium text-sm text-charcoal/70">
          <span>Gün</span>
          <span>Açılış</span>
          <span>Kapanış</span>
          <span className="text-center">Durum</span>
        </div>
        {DAY_ORDER.map(day => {
          const d = hours[day] || { open: '09:00', close: '23:00', is_closed: false };
          return (
            <div key={day} className={`grid grid-cols-[140px_1fr_1fr_80px] gap-4 p-4 items-center border-b last:border-b-0 transition-colors ${d.is_closed ? 'bg-red-50/50' : ''}`}
              data-testid={`day-row-${day}`}>
              <span className="font-medium text-charcoal">{DAY_LABELS[day]}</span>
              <Input
                type="time"
                value={d.open}
                onChange={e => updateDay(day, 'open', e.target.value)}
                disabled={d.is_closed}
                className={d.is_closed ? 'opacity-40' : ''}
                data-testid={`open-${day}`}
              />
              <Input
                type="time"
                value={d.close}
                onChange={e => updateDay(day, 'close', e.target.value)}
                disabled={d.is_closed}
                className={d.is_closed ? 'opacity-40' : ''}
                data-testid={`close-${day}`}
              />
              <button
                onClick={() => toggleClosed(day)}
                className="flex justify-center"
                data-testid={`toggle-${day}`}
              >
                {d.is_closed ? (
                  <span className="flex items-center gap-1 text-red-500 text-sm font-medium">
                    <ToggleLeft className="w-6 h-6" /> Kapalı
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-basil text-sm font-medium">
                    <ToggleRight className="w-6 h-6" /> Açık
                  </span>
                )}
              </button>
            </div>
          );
        })}
      </div>

      <div className="bg-sand/50 rounded-xl p-4 text-sm text-charcoal/70">
        <p><strong>Not:</strong> Saat dilimi: Europe/Podgorica (CET). Kapanış saati 00:00 gece yarısı anlamına gelir.</p>
      </div>
    </div>
  );
};

export default SettingsManagement;
